// exportService - Client-side PDF generation for retrospective export
// Implements US-4.1: Export Retrospective as PDF (ephemeral, no storage)

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Card, CardGroup, Room, Template } from '../types/card';

interface ExportData {
  room: Room;
  template: Template;
  cards: Card[];
  groups: CardGroup[];
  participantCount: number;
}

/**
 * GENERATE PDF from retrospective data
 * Two-part PDF:
 * - Page 1: Visual board screenshot (all cards expanded)
 * - Page 2+: Structured text summary
 */
export async function generatePDF(data: ExportData): Promise<Blob> {
  // CREATE new PDF document
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  let currentY = 20; // TRACK current Y position for page layout

  try {
    // ATTEMPT to capture board screenshot for Page 1
    const boardElement = document.querySelector('[data-export-board]') as HTMLElement;

    if (boardElement) {
      // EXPAND all cards temporarily for better screenshot readability
      const collapsedCards = expandAllCards();

      // CAPTURE board as high-resolution image
      const canvas = await html2canvas(boardElement, {
        scale: 2, // 2x resolution for crisp text
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      });

      // RESTORE collapsed state
      restoreCollapsedCards(collapsedCards);

      // CONVERT canvas to image data
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 277; // A4 landscape width in mm (minus margins)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // ADD screenshot to Page 1
      pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);

      // ADD new page for structured text
      pdf.addPage();
      currentY = 20;
    } else {
      console.warn('Board element not found - generating text-only PDF');
    }
  } catch (error) {
    console.error('Failed to capture board screenshot:', error);
    // FALLBACK to text-only PDF (Page 2+ only)
  }

  // GENERATE structured text pages (Page 2+)
  generateStructuredPages(pdf, data, currentY);

  // RETURN PDF as blob
  return pdf.output('blob');
}

/**
 * GENERATE structured text summary pages
 */
function generateStructuredPages(
  pdf: jsPDF,
  data: ExportData,
  startY: number
): void {
  const { room, template, cards, groups, participantCount } = data;
  let y = startY;

  // HEADER section
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text(room.name || `${template.name} Retrospektive`, 10, y);
  y += 10;

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Template: ${template.name}`, 10, y);
  y += 5;
  pdf.text(`Datum: ${new Date(room.createdAt).toLocaleDateString()}`, 10, y);
  y += 5;
  pdf.text(`Teilnehmer: ${participantCount}`, 10, y);
  y += 10;

  // SEPARATOR line
  pdf.setDrawColor(200, 200, 200);
  pdf.line(10, y, 287, y);
  y += 10;

  // ORGANIZE cards by column
  const cardsByColumn = organizeCardsByColumn(cards, groups, template);

  // RENDER each column section
  for (const columnData of cardsByColumn) {
    // CHECK if we need a new page
    if (y > 180) {
      pdf.addPage();
      y = 20;
    }

    // COLUMN header
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(columnData.label, 10, y);
    y += 7;

    // RENDER cards in this column
    for (const item of columnData.items) {
      // CHECK if we need a new page
      if (y > 180) {
        pdf.addPage();
        y = 20;
      }

      if (item.type === 'group') {
        // GROUP header
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(147, 112, 219); // Purple for groups
        const groupLabel = item.label || 'Gruppierte Karten';
        pdf.text(`[Gruppe] ${groupLabel}`, 15, y);
        y += 6;

        // RENDER cards in group
        for (const card of item.cards) {
          y = renderCard(pdf, card, y, 20); // Indent grouped cards
        }
      } else {
        // RENDER single card
        y = renderCard(pdf, item.card, y, 15);
      }
    }

    y += 5; // Space between columns
  }

  // ACTION ITEMS section (if any)
  const actionItems = cards.filter((c) => c.column === 'actions');
  if (actionItems.length > 0) {
    // CHECK if we need a new page
    if (y > 160) {
      pdf.addPage();
      y = 20;
    }

    y += 5;
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(139, 92, 246); // Purple for action items
    pdf.text('Action Items', 10, y);
    y += 10;

    for (const action of actionItems) {
      // CHECK if we need a new page
      if (y > 180) {
        pdf.addPage();
        y = 20;
      }

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);

      // ACTION text
      const textLines = pdf.splitTextToSize(`• ${action.text}`, 260);
      pdf.text(textLines, 15, y);
      y += textLines.length * 5;

      // OWNER and due date
      if (action.owner || action.dueDate) {
        pdf.setFontSize(9);
        pdf.setTextColor(100, 100, 100);
        const metadata = [];
        if (action.owner) metadata.push(`Verantwortlich: ${action.owner}`);
        if (action.dueDate) {
          metadata.push(`Fällig: ${new Date(action.dueDate).toLocaleDateString()}`);
        }
        pdf.text(metadata.join(' | '), 20, y);
        y += 5;
      }

      y += 2;
    }
  }

  // FACILITATOR NOTES section (if any) - US-2.5
  const notes = cards.filter((c) => c.column === 'notes');
  if (notes.length > 0) {
    // CHECK if we need a new page
    if (y > 160) {
      pdf.addPage();
      y = 20;
    }

    y += 5;
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(245, 158, 11); // Orange for facilitator notes
    pdf.text('Facilitator-Notizen', 10, y);
    y += 10;

    for (const note of notes) {
      // CHECK if we need a new page
      if (y > 180) {
        pdf.addPage();
        y = 20;
      }

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);

      // NOTE text
      const textLines = pdf.splitTextToSize(`• ${note.text}`, 260);
      pdf.text(textLines, 15, y);
      y += textLines.length * 5;

      // TIMESTAMP
      pdf.setFontSize(9);
      pdf.setTextColor(100, 100, 100);
      const timestamp = new Date(note.createdAt).toLocaleString();
      pdf.text(`Erstellt: ${timestamp}`, 20, y);
      y += 5;

      y += 2;
    }
  }

  // FOOTER with timestamp
  const pageCount = pdf.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.text(
      `Exportiert: ${new Date().toLocaleString()}`,
      10,
      200,
      { align: 'left' }
    );
    pdf.text(`Seite ${i} von ${pageCount}`, 287, 200, { align: 'right' });
  }
}

/**
 * RENDER a single card in PDF
 */
function renderCard(pdf: jsPDF, card: Card, y: number, indent: number): number {
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(0, 0, 0);

  // CARD text with vote count and discussed indicator
  let cardText = `- ${card.text}`;
  if (card.voteCount > 0) {
    cardText += ` [${card.voteCount} Stimmen]`;
  }
  if (card.discussed) {
    cardText += ` [Besprochen]`;
  }

  const textLines = pdf.splitTextToSize(cardText, 260 - indent);
  pdf.text(textLines, indent, y);
  y += textLines.length * 5;

  // AUTHOR name (if not anonymous)
  if (card.authorName) {
    pdf.setFontSize(8);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`- ${card.authorName}`, indent + 5, y);
    y += 4;
  }

  y += 2;
  return y;
}

/**
 * ORGANIZE cards by column with groups
 */
function organizeCardsByColumn(
  cards: Card[],
  groups: CardGroup[],
  template: Template
): Array<{
  label: string;
  items: Array<
    | { type: 'card'; card: Card }
    | { type: 'group'; label: string | null; cards: Card[] }
  >;
}> {
  const result: Array<{
    label: string;
    items: Array<
      | { type: 'card'; card: Card }
      | { type: 'group'; label: string | null; cards: Card[] }
    >;
  }> = [];

  // FILTER out action items and notes columns (handled separately)
  const retroColumns = template.columns.filter((col) => col.key !== 'actions');

  for (const column of retroColumns) {
    const columnCards = cards.filter(
      (c) => c.column === column.key && c.groupId === null && c.column !== 'notes'
    );
    const columnGroups = groups.filter((g) => g.column === column.key);

    const items: Array<
      | { type: 'card'; card: Card }
      | { type: 'group'; label: string | null; cards: Card[] }
    > = [];

    // ADD ungrouped cards
    for (const card of columnCards) {
      items.push({ type: 'card', card });
    }

    // ADD grouped cards
    for (const group of columnGroups) {
      const groupCards = cards.filter((c) => c.groupId === group.id);
      if (groupCards.length > 0) {
        items.push({
          type: 'group',
          label: group.label,
          cards: groupCards,
        });
      }
    }

    if (items.length > 0) {
      result.push({
        label: column.label,
        items,
      });
    }
  }

  return result;
}

/**
 * EXPAND all collapsed cards for screenshot
 * Returns list of card IDs that were collapsed
 */
function expandAllCards(): string[] {
  const collapsedCardIds: string[] = [];
  const cardElements = document.querySelectorAll('[data-card-id]');

  cardElements.forEach((el) => {
    const cardId = el.getAttribute('data-card-id');
    const textElement = el.querySelector('.card-text');

    if (textElement && textElement.classList.contains('collapsed')) {
      textElement.classList.remove('collapsed');
      textElement.classList.add('expanded');
      if (cardId) collapsedCardIds.push(cardId);
    }
  });

  return collapsedCardIds;
}

/**
 * RESTORE collapsed state for specified cards
 */
function restoreCollapsedCards(cardIds: string[]): void {
  cardIds.forEach((cardId) => {
    const cardElement = document.querySelector(`[data-card-id="${cardId}"]`);
    if (cardElement) {
      const textElement = cardElement.querySelector('.card-text');
      if (textElement) {
        textElement.classList.remove('expanded');
        textElement.classList.add('collapsed');
      }
    }
  });
}

/**
 * DOWNLOAD PDF blob to user's device
 */
export function downloadPDF(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * GENERATE filename for export
 */
export function generateFilename(room: Room, template: Template): string {
  const roomName = room.name || template.name;
  const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const sanitized = roomName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  return `retro_${sanitized}_${date}.pdf`;
}
